'use client';

import type { ReactNode } from 'react';
import React, { useMemo, useRef } from 'react';
import { useId } from 'react-aria';
import { Portal } from 'components/Portal';
import type { PortalProps } from 'components/Portal/Portal';
import { getScrollStyles } from 'components/ScrollContainer/ScrollContainer';
import { useBodyScrollLock } from 'hooks/useBodyScrollLock';
import { useFocusTrap } from 'hooks/useFocusTrap';
import { isComposingEvent } from 'utils/is-composing';
import { noop } from 'utils/noop';
import { useExitAnimation } from '@react-aria/utils';
import { theme } from '@frameio/vapor-theme';
import {
  MODAL_ENTER_DURATION,
  MODAL_EXIT_DURATION,
} from './animation-constants';
import styles from './DialogContainer.module.css';
import { DialogContext } from './DialogContext';
import { Overlay } from './Overlay';
import type {
  DialogMobileVariant,
  DialogPositions,
  OnRequestClose,
} from './types';
import { useHideBody } from './useHideBody';
import { useOverlayFocus } from './useOverlayFocus';
import { useTimeoutCallbacks } from './useTimeoutCallbacks';

export type DialogContainerProps = {
  /** The content of the dialog. */
  children?: (
    ref: React.Ref<HTMLDivElement>,
    state: {
      isExiting: boolean;
    },
    id: string
  ) => ReactNode;
  /** Control whether the dialog is showing. */
  isShowing: boolean;
  /** A callback to indicate that the dialog should close. */
  onRequestClose?: OnRequestClose;
  /** An optional element ref to focus on mount. Eg., a save button. */
  initialFocusRef?: any;
  /** An optional element ref to return focus to when the dialog closes. */
  returnFocusRef?: React.RefObject<HTMLElement | null>;
  /** Enable clicking on the overlay to close it. */
  enableClickOutsideToClose?: boolean;
  /** A configurable z-index. Generally you shouldn't touch this,
   * because it can have implicatations for the z-index of subsequent
   * panels.
   */
  zIndex?: number;
  /** Vertical position within the page where the Dialog will sit. At smaller viewport heights this will be ignored. */
  position?: DialogPositions;
  /** Callback fired when the exit animation completes. */
  onExited?: () => void;
  /** Callback fired when the enter animation completes. */
  onEntered?: () => void;
  /** Whether to show a focus ring when the dialog is focused. Used primarily
   * for AlertDialogs.
   */
  showFocusRing?: boolean;
  /** Disable the dialog entry and exit animations */
  disableAnimation?: boolean;
  /** Mobile presentation variant */
  mobileVariant?: DialogMobileVariant;
} & Pick<PortalProps, 'hasOutermostTheme'>;

export function DialogContainer({
  initialFocusRef,
  returnFocusRef,
  isShowing,
  children,
  onRequestClose = noop,
  zIndex = theme.zIndices.modal,
  enableClickOutsideToClose = true,
  position = 'centered',
  hasOutermostTheme,
  onExited = noop,
  onEntered = noop,
  showFocusRing = false,
  disableAnimation = false,
  mobileVariant = 'default',
}: DialogContainerProps) {
  const id = useId();
  const ref = useRef<HTMLDivElement>(null);
  const rootRef = useRef(null);

  // trap focus inside our dialog
  const handleTab = useFocusTrap(ref);

  // manage focus
  const { onFocus } = useOverlayFocus(ref, {
    initialFocusRef,
    returnFocusRef,
    isShowing,
    showFocusRing,
  });

  // lock body scrolling
  useBodyScrollLock({ enabled: isShowing });

  // set aria-hidden to all root elements besides this one when showing
  useHideBody(rootRef, isShowing);

  // close dialog when escape is pressed
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    handleTab(e);
    if (e.key === 'Escape') {
      // Prevent closing the dialog while composing, allowing the browser to handle the composition event
      if (isComposingEvent(e)) return;
      // if we have a dialog within a dialog, only close
      // the first one
      e.stopPropagation();
      onRequestClose(e);
    }
  };

  const dialogContext = useMemo(
    () => ({
      onRequestClose,
      id,
    }),
    [onRequestClose, id]
  );

  function onContainerClick(e: React.MouseEvent) {
    if (e.target === rootRef.current && enableClickOutsideToClose) {
      onRequestClose(e);
    }
  }

  const { paddingLeft, paddingRight } = getScrollStyles(8);

  const handleOnClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const containerHandleOnClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const isExiting = useExitAnimation(ref, isShowing);

  useTimeoutCallbacks({
    isShowing,
    onExited,
    onEntered,
    enterDuration: MODAL_ENTER_DURATION,
    exitDuration: MODAL_EXIT_DURATION,
    disableAnimation,
  });

  if ((!isShowing && !isExiting) || !children) {
    return null;
  }

  return (
    <Portal hasOutermostTheme={hasOutermostTheme}>
      <DialogContext.Provider value={dialogContext}>
        <Overlay
          isShowing={isShowing}
          onClick={handleOnClick}
          zIndex={zIndex}
        />
        <div
          ref={rootRef}
          className={styles.container}
          data-mobile-variant={mobileVariant}
          data-position={position}
          data-testid="dialog-container"
          onClick={containerHandleOnClick}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          onMouseDown={onContainerClick}
          style={
            {
              '--vapor-dialog-padding-inline-start': `${paddingLeft}px`,
              '--vapor-dialog-padding-inline-end': `${paddingRight}px`,
              '--vapor-dialog-z-index': zIndex + 1,
            } as React.CSSProperties
          }
        >
          {children(
            ref,
            {
              isExiting,
            },
            id
          )}
        </div>
      </DialogContext.Provider>
    </Portal>
  );
}
